import { useState, useContext, useEffect } from 'react';

import { UserContext } from 'components/signUpFunnel/Contexts';
import PreferencesFormSkeleton from 'components/signUpFunnel/preferences/PreferencesFormSkeleton';
import DietaryPreferencesForm from 'components/signUpFunnel/shared/DietaryPreferencesForm';
import useAnalyticsCallback from 'hooks/useAnalyticsCallback';
import useAnalyticsEffect from 'hooks/useAnalyticsEffect';
import { TEMPO } from 'lib/constants';
import * as ErrorLogging from 'services/ErrorLogging';
import { updatePreferences, getPreferences } from 'services/RequestService/endpoints/signUp';

import ContinueButton from './ContinueButton';

const DietaryPreferencesContainer = ({ goNext }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [preferences, setPreferences] = useState(null);
  const [dietaryPreferenceIds, setDietaryPreferenceIds] = useState(null);
  const [allergyIds, setAllergyIds] = useState(null);

  const { user, setUser } = useContext(UserContext);

  useEffect(() => {
    getPreferences().then(response => {
      if (response.ok) {
        setPreferences(response.preferences);
      }
    });
  }, []);

  useEffect(() => {
    if (preferences?.dietaryPreferences) {
      setDietaryPreferenceIds(
        preferences.dietaryPreferences
          .filter(dietaryPreference => dietaryPreference.selected === true)
          .map(dietaryPreference => dietaryPreference.id)
      );
    }
  }, [preferences?.dietaryPreferences]);

  useEffect(() => {
    if (preferences?.allergies) {
      setAllergyIds(
        preferences.allergies
          .filter(allergy => allergy.selected === true)
          .map(allergy => allergy.id)
      );
    }
  }, [preferences?.allergies]);

  useAnalyticsEffect(({ events, logEvent }) => {
    logEvent(events.pickMeals[TEMPO].preferences.viewed, { mealPlanSlug: TEMPO });
  }, []);

  const handlePreferencesClick = ({ friendlyName, selected }) => {
    const newDietaryPreferenceState = preferences.dietaryPreferences.map(dietaryPreference => {
      if (dietaryPreference.friendlyName === friendlyName) {
        return { ...dietaryPreference, selected: !selected };
      }
      return dietaryPreference;
    });

    setPreferences({ dietaryPreferences: newDietaryPreferenceState });
  };

  const handleOnClick = useAnalyticsCallback(
    async () => {
      try {
        setIsSubmitting(true);
        const response = await updatePreferences({ params: { allergyIds, dietaryPreferenceIds } });

        let selectedPreferences = [];
        if (response.ok && response.preferences?.dietaryPreferences) {
          selectedPreferences = response.preferences.dietaryPreferences
            .filter(pref => pref.selected)
            .map(pref => pref.friendlyName);

          setUser(prevUser => ({
            ...prevUser,
            dietaryPreferenceNames: selectedPreferences,
            optIns: response.optIns,
          }));
        }

        goNext();

        return { selectedPreferences };
      } catch (error) {
        ErrorLogging.captureException(error.message);
      }
    },
    ({ logEvent, events, callbackFnResult }) => {
      const { selectedPreferences } = callbackFnResult || {};
      logEvent(events.pickMeals[TEMPO].preferences.submitted, {
        mealPlanSlug: TEMPO,
        preferencesSelected: selectedPreferences,
      });
    },
    [allergyIds, dietaryPreferenceIds]
  );

  return (
    <>
      {user && preferences?.dietaryPreferences ? (
        <>
          <DietaryPreferencesForm
            preferences={preferences}
            handlePreferencesClick={handlePreferencesClick}
          />
          <ContinueButton
            onContinue={handleOnClick}
            disabled={false}
            submittingText="Continue"
            isSubmitting={isSubmitting}
          />
        </>
      ) : (
        <PreferencesFormSkeleton skeletonClassName="bg-tempo-gray-40" skeletonCount={3} />
      )}
    </>
  );
};

DietaryPreferencesContainer.propTypes = {
  goNext: PropTypes.func,
};

export default DietaryPreferencesContainer;
